import { ReactNode, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { faBan, faDownload } from '@fortawesome/pro-regular-svg-icons';
import { faBan as faBanSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { revokeTicket } from 'api/tickets';
import * as ChangeContact from 'assets/js/pages/contacts/single/timeline/timeline-list/change-contact';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Form from 'components/form';
import * as ResendTicketDialog from 'components/pages/tickets/ResendTicketDialog';
import {
    TicketActionsContext,
    useTicketActionsContext,
} from 'components/pages/tickets/TicketActionsContext';
import * as ResendItemDialog from 'components/resend-item-dialog';
import { UnstyledAnchor } from 'components/unstyled-link';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { transactionQueryKeys } from 'queries/transaction-query-keys';
import { twJoin } from 'tailwind-merge';
import { BundledTicket, Ticket } from 'types/domain';

type TicketItems = Ticket | BundledTicket;

interface TicketActionsProviderProps {
    ticket: TicketItems;
    children: ReactNode;
}

const TicketActionsProvider = ({ ticket, children }: TicketActionsProviderProps) => {
    const value = useMemo(() => ({ ticket }), [ticket]);

    return <TicketActionsContext.Provider value={value}>{children}</TicketActionsContext.Provider>;
};

const isBundledTicket = (ticket: any): ticket is BundledTicket => {
    return !!ticket.bundle;
};

const TicketActionsRevoke = () => {
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();
    const { ticket } = useTicketActionsContext();

    const { success, error } = useToast();

    const { mutate: revoke } = useMutation({
        mutationFn: () => revokeTicket(ticket.id),
        onSuccess: () => {
            success('Ticket revoked successfully');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.tickets(accountId) });
            queryClient.invalidateQueries({
                queryKey: transactionQueryKeys.transactions(accountId),
            });
        },
        onError: () => {
            error('Could not revoke ticket. Please try again.');
        },
    });

    return (
        <AlertDialog.Root type={'error'}>
            <AlertDialog.Trigger asChild={true}>
                <DropdownMenu.Item
                    disabled={Boolean(ticket.revoked_at)}
                    onSelect={e => e.preventDefault()}
                    className="flex items-center gap-2"
                >
                    <FontAwesomeIcon size={'sm'} icon={faBan} />
                    <p>Revoke</p>
                </DropdownMenu.Item>
            </AlertDialog.Trigger>
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon customIcon={faBanSolid} />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Revoke Ticket?</AlertDialog.Title>
                        <AlertDialog.Description>
                            You can't undo this action afterwards.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                        <AlertDialog.Action onClick={() => revoke()}>
                            Revoke Ticket
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const TicketActionsViewTicket = () => {
    const { accountId } = useCurrentAccount();
    const { ticket } = useTicketActionsContext();

    return (
        <DropdownMenu.LinkItem to={`/accounts/${accountId}/tickets/${ticket?.id}`}>
            View ticket details
        </DropdownMenu.LinkItem>
    );
};

const TicketActionsViewTransaction = () => {
    const { accountId } = useCurrentAccount();
    const { ticket } = useTicketActionsContext();

    return (
        <DropdownMenu.LinkItem to={`/accounts/${accountId}/transactions/${ticket.transaction_id}`}>
            View transaction
        </DropdownMenu.LinkItem>
    );
};

const TicketActionsReassign = () => {
    const { success } = useToast();
    const { accountId } = useCurrentAccount();
    const { ticket } = useTicketActionsContext();
    const queryClient = useQueryClient();

    if (!ticket.contact) {
        return null;
    }

    const isReassignDisabled = !!ticket.revoked_at || !!ticket.checked_in_at;

    return (
        <ChangeContact.Root
            key={ticket.contact.id}
            activity={{
                source: 'system',
                type: 'ticket.issued',
                resource: {
                    id: ticket.id,
                },
            }}
            contact={ticket.contact}
            onSuccess={() => {
                success('Ticket reassigned successfully');
                queryClient.invalidateQueries({
                    queryKey: accountQueryKeys.tickets(accountId),
                });
                queryClient.invalidateQueries({
                    queryKey: transactionQueryKeys.transactions(accountId),
                });
            }}
        >
            <ChangeContact.Trigger asChild>
                <DropdownMenu.Item disabled={isReassignDisabled} onSelect={e => e.preventDefault()}>
                    Reassign
                </DropdownMenu.Item>
            </ChangeContact.Trigger>
        </ChangeContact.Root>
    );
};

interface ResendFormValues {
    email: string;
}

const TicketActionsResend = () => {
    const { ticket } = useTicketActionsContext();

    const itemEmail = isBundledTicket(ticket) ? ticket.transaction?.email : ticket.email;

    const methods = Form.useGivebutterForm<ResendFormValues>({
        defaultValues: {
            email: itemEmail ?? '',
        },
    });

    return (
        <Form.Provider {...methods}>
            <ResendTicketDialog.Root>
                <ResendItemDialog.DropdownItem />
                <ResendItemDialog.Portal>
                    <ResendItemDialog.Overlay />
                    <ResendItemDialog.Content>
                        <ResendItemDialog.Icon />
                        <ResendItemDialog.Header />
                        <ResendItemDialog.FormBody />
                    </ResendItemDialog.Content>
                </ResendItemDialog.Portal>
            </ResendTicketDialog.Root>
        </Form.Provider>
    );
};

const TicketActionsDownload = () => {
    const { ticket } = useTicketActionsContext();

    const downloadText = isBundledTicket(ticket) ? 'Download All' : 'Download';
    const isDisabled = !ticket.pdf;

    return (
        <UnstyledAnchor
            className={twJoin(isDisabled && 'pointer-events-none')}
            href={ticket?.pdf}
            rel="noopener noreferrer"
            target={'_blank'}
        >
            <DropdownMenu.Item disabled={isDisabled} className="flex items-center gap-2">
                <FontAwesomeIcon size={'sm'} icon={faDownload} />
                <p>{downloadText}</p>
            </DropdownMenu.Item>
        </UnstyledAnchor>
    );
};

const Root = TicketActionsProvider;
const Revoke = TicketActionsRevoke;
const Reassign = TicketActionsReassign;
const Resend = TicketActionsResend;
const ViewTransaction = TicketActionsViewTransaction;
const ViewTicket = TicketActionsViewTicket;
const Download = TicketActionsDownload;

export {
    TicketActionsProvider,
    TicketActionsRevoke,
    TicketActionsReassign,
    TicketActionsResend,
    TicketActionsViewTicket,
    TicketActionsViewTransaction,
    TicketActionsDownload,
    //
    Root,
    Revoke,
    Reassign,
    Resend,
    ViewTicket,
    ViewTransaction,
    Download,
};
